/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Select,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/store";
import DateIcon from "../../../assets/icons/DateIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import UserImage from "../../../assets/images/avatar.svg";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import {
  useAddAdjustmentMutation,
  useGetLeaveAdjustmentDetailsQuery,
  useGetLeaveTypeListQuery,
  useUpdateAdjustmentMutation,
} from "../../../features/leaveType/leaveTypeApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import { selectSearchOption } from "../../../utils/utils";
import ListWithCheckbox from "../../Attendance/CustomAttendance/_components/ListWithCheckbox";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

export type ListComponentType = {
  form: FormInstance<any>;
};

export default function ManageAdjustmentModal({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<number | undefined>) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const {
    data: leaveTypeListData,
    isLoading: isLeaveTypeListLoading,
    isError: isLeaveError,
    error: leaveError,
  } = useGetLeaveTypeListQuery({
    params: {
      limit: 0,
      skip: 0,
      is_published: 1,
    },
  });
  useError(isLeaveError, leaveError, "Failed fetching leave type");

  const [
    updateAdjustment,
    {
      isSuccess: isUpdateSuccess,
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateAdjustmentMutation();
  const [addAdjustment, { isSuccess: isAddLeaveSuccess, isError, error }] =
    useAddAdjustmentMutation();
  const {
    data: detailsData,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
  } = useGetLeaveAdjustmentDetailsQuery(Number(data), {
    skip: type === "ADD" || !data,
  });

  useError(isError, error, "Adjustment added fail");
  useError(isUpdateError, updateError, "Adjustment updated fail");
  useError(isDetailsError, detailsError, "Get adjustment fail");

  useEffect(() => {
    if (type === "UPDATE") {
      const details = detailsData?.data?.leave_adjustment_policy;
      form.setFieldsValue({
        rule_name: details?.rule_name,
        leave_sequences: leaveTypeListData?.data?.leave_types?.map(
          (el: any) => {
            const findData = details?.leave_types?.find(
              (item) => item?.leave_type_id === el?.id
            );

            return {
              sequence: findData?.sequence,
              leave_type_id: el?.id,
            };
          }
        ),
        applicable_for: details?.applicable_for,
        effective_from: dayjs(details?.effective_from, dbDateFormat),
        target_ids_department:
          details?.applicable_for === "department"
            ? details?.targets?.map((el) => el?.target_id)
            : undefined,
        target_ids_employee:
          details?.applicable_for === "employee"
            ? details?.targets?.map((el) => el?.target_id)
            : undefined,
      });
    }
  }, [form, detailsData?.data?.leave_adjustment_policy, type]);

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      effective_from: values?.effective_from?.format(dbDateFormat),
      target_ids:
        values?.applicable_for === "department"
          ? values?.target_ids_department
          : values?.applicable_for === "employee"
          ? values?.target_ids_employee
          : undefined,
      target_ids_department: undefined,
      target_ids_employee: undefined,
    };

    if (type === "UPDATE") {
      updateAdjustment({
        id: data,
        data: newData,
      });
    } else {
      addAdjustment(newData);
    }
  };

  useEffect(() => {
    if (isAddLeaveSuccess) {
      toast("success", "Adjustment added successfully");
      handleClose();
    }
  }, [isAddLeaveSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "Adjustment updated successfully");
      handleClose();
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  }, []);

  return (
    <CustomModal
      title={type === "UPDATE" ? "Update Policy" : "Add Policy"}
      {...rest}
      handleClose={handleClose}
      width={556}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isDetailsLoading || isLeaveTypeListLoading || loading ? (
          <Loader />
        ) : (
          <>
            <Form.Item
              name="rule_name"
              label="Rule Name"
              className="mb-2.5"
              rules={[
                {
                  required: true,
                  message: "Please enter rule name",
                },
              ]}
            >
              <Select
                options={rules}
                placeholder="Select"
                suffixIcon={<SelectDropdownLgIcon />}
              />
              {/* <Input size="large" placeholder="Enter rule name" /> */}
            </Form.Item>

            <div>
              <label className="text-sm font-medium text-body">
                Adjustment Sequence
              </label>

              {isLeaveTypeListLoading ? (
                <Loader />
              ) : (
                <Form.List name="leave_sequences">
                  {() => (
                    <>
                      {leaveTypeListData?.data?.leave_types?.map(
                        (field: any, index: number) => (
                          <div key={index} className="px-2 py-1.5">
                            <Form.Item
                              name={[index, "leave_type_id"]}
                              className="sr-only"
                              initialValue={field?.id}
                            >
                              <Input className="w-full" size="large" disabled />
                            </Form.Item>

                            <div className="flex justify-between items-center">
                              <label>
                                <div className="text-body-text text-sm font-normal">
                                  {field?.title}
                                </div>
                              </label>

                              <Form.Item
                                name={[index, "sequence"]}
                                className="mb-0"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select sequence",
                                  },
                                ]}
                              >
                                <Select
                                  size="small"
                                  showSearch
                                  filterOption={selectSearchOption}
                                  options={[
                                    ...Array(
                                      leaveTypeListData?.data
                                        ?.total_leave_types || 0
                                    ),
                                  ].map((_, i) => ({
                                    label: (i + 1).toString(),
                                    value: i + 1,
                                  }))}
                                  className="min-w-[80px]"
                                  suffixIcon={<SelectDownIcon />}
                                  placeholder="Select"
                                />
                              </Form.Item>
                            </div>
                          </div>
                        )
                      )}
                    </>
                  )}
                </Form.List>
              )}
            </div>

            <Form.Item
              name="applicable_for"
              label="Applicable For"
              className="mb-2.5"
              rules={[
                {
                  required: true,
                  message: "Please select applicable for",
                },
              ]}
              initialValue={"global"}
            >
              <Select
                placeholder="Select"
                options={applicationList}
                suffixIcon={<SelectDropdownLgIcon />}
              />
            </Form.Item>

            <CalculateType form={form} />

            <Form.Item
              name="effective_from"
              label="Effective From"
              className="mb-1.5"
              rules={[
                {
                  required: true,
                  message: "Please select effective from",
                },
              ]}
            >
              <DatePicker
                format={dateFormat}
                suffixIcon={<DateIcon width={18} height={20} />}
                style={{ width: "100%" }}
                size="large"
                placeholder="Select date"
              />
            </Form.Item>

            <Space className="w-full mt-2" size={12}>
              <Button
                className="normal-dark-button modal-button"
                onClick={handleClose}
                size="middle"
              >
                Cancel
              </Button>
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={isUpdateLoading}
              >
                {type === "UPDATE" ? "Update" : "Save"}
              </Button>
            </Space>
          </>
        )}
      </Form>
    </CustomModal>
  );
}

const applicationList = [
  {
    label: "Global",
    value: "global",
  },
  {
    label: "Department",
    value: "department",
  },
  {
    label: "Employee",
    value: "employee",
  },
];

const CalculateType = ({ form }: ListComponentType) => {
  const applicableFor = Form.useWatch(["applicable_for"], form);
  const { authInfo } = useAppSelector((state) => state.auth);
  const { isError, error, data, isLoading } = useGetEmployeesQuery({
    params: {
      status: "active",
      manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
      skip: 0,
      limit: 0,
    },
  });
  const {
    data: departmentData,
    isError: isDepartmentError,
    error: departmentError,
    isLoading: isDepartmentLoading,
  } = useGetDepartmentsQuery(
    {
      params: { limit: 0, skip: 0, is_published: 1 },
    },
    {
      skip: applicableFor !== "department",
    }
  );
  useError(isDepartmentError, departmentError, "Get department fail");
  useError(isError, error, "Get employee fail");

  const employeeItems = Form.useWatch("target_ids_employee", form);

  const filteredEmployees = data?.data?.employees?.filter((el) => {
    const id = el?.id;
    return id != null && employeeItems?.find((item: number) => item === id);
  });

  const removeSelectedEmployee = (id: number) => {
    const employee = employeeItems?.filter((item: number) => item !== id);
    form.setFieldsValue({
      target_ids_employee: employee,
    });
  };

  if (isDepartmentLoading || isLoading) {
    return <Loader />;
  }

  return applicableFor === "department" ? (
    <div>
      <Form.Item
        name="target_ids_department"
        className="mb-2.5"
        rules={[
          {
            required: true,
            message: "Please select department",
          },
        ]}
      >
        <Checkbox.Group className="grid grid-cols-3  gap-x-1 gap-y-2 p-2.5">
          {departmentData?.data?.departments.map((item) => (
            <Checkbox
              key={item.id}
              value={item.id}
              className="text-header !text-sm font-medium"
            >
              {item.name}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
    </div>
  ) : applicableFor === "employee" ? (
    <div>
      <Form.Item
        name="target_ids_employee"
        label="Select Employee"
        className="mb-2.5"
        rules={[
          {
            required: true,
            message: "Please select employee",
          },
        ]}
      >
        <ListWithCheckbox
          size="large"
          maxTagCount={2}
          suffixIcon={<SelectDropdownLgIcon />}
          list={
            data?.data?.employees?.map((el) => ({
              label: el?.member?.profile?.name,
              opLabel: el?.employee_id || "n/a",
              value: el?.id,
            })) || []
          }
        />
      </Form.Item>

      <div className="mb-2.5 space-y-2.5">
        {filteredEmployees?.map((el) => (
          <div
            className="p-3 flex justify-between items-center rounded outline outline-1 outline-offset-[-1px] outline-light"
            key={el?.id}
          >
            <div className="flex-1 flex items-center gap-3">
              <img
                className="w-10 h-10 rounded-full"
                src={el?.member?.profile?.pro_pic || UserImage}
                alt=""
              />
              <div>
                <h3 className="text-body text-sm font-medium">
                  {el?.member?.profile?.name}
                </h3>
                <h6 className="text-display-body text-xs font-medium">
                  {el?.role?.name}
                </h6>
              </div>
            </div>

            <Button
              className="action-button"
              size="small"
              icon={<DeleteIcon />}
              onClick={() => removeSelectedEmployee(el?.id)}
            />
          </div>
        ))}
      </div>
    </div>
  ) : null;
};

export const rules = [
  {
    label: "Absent adjustment",
    value: "absent_adjustment",
  },
  {
    label: "Late check in out adjustment",
    value: "late_check_in_out_adjustment",
  },
];
